import { Component, OnInit, Input, OnChanges, SimpleChanges , DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { PaymentTrackingItemService, PaymentTrackingItem, CreatePaymentTrackingItemDto } from '../../services/payment-tracking-item.service';
import { ProjectService } from '../../services/project.service';

@Component({
  selector: 'app-payment-tracking',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Tag,
    Tooltip,
    Dialog,
    InputText,
    InputNumber,
    Select,
    DatePicker,
    ConfirmDialog,
  ],
  templateUrl: './payment-tracking.component.html',
  styleUrl: './payment-tracking.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class PaymentTrackingComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  @Input() projectId: string = '';
  @Input() filterDate: string | null = null; // Filter by target date (YYYY-MM-DD format)
  @Input() readonly = false; // When true (e.g. Sales), hide Add + Actions (view-only)

  items: PaymentTrackingItem[] = [];
  filteredItems: PaymentTrackingItem[] = [];
  selectedItem: PaymentTrackingItem | null = null;
  displayItemDialog: boolean = false;
  isEditItemMode: boolean = false;
  targetDate: Date | null = null;
  invoicedDate: Date | null = null;
  paymentDueDate: Date | null = null;
  paidDate: Date | null = null;
  loading: boolean = false;
  fieldTouched: Record<string, boolean> = {};

  /**
   * True while the Payment Due Date in the open dialog is one this component calculated, so it may
   * be recalculated freely. Set false the moment the user edits that field by hand — from then on
   * their date is an agreed extension and survives any later term change.
   */
  private dueDateIsDerived = false;

  /** Guards the datepicker's change handler while this component writes the due date itself. */
  private writingDerivedDueDate = false;

  /**
   * Project's default payment term, fetched from the project itself.
   * <p>
   * An earlier version inferred this from the returned rows (a row with no override reports the
   * default as its effective term). That silently failed whenever EVERY row carried its own
   * override — the dialog then claimed "No project default set" while a default existed.
   * </p>
   */
  projectPaymentTermDays: number | null = null;

  statusOptions = [
    { label: 'Open', value: 'Open' },
    { label: 'Ready for Invoice', value: 'Ready for Invoice' },
    { label: 'Invoiced', value: 'Invoiced' },
    { label: 'Paid', value: 'Paid' }
  ];

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private paymentTrackingItemService: PaymentTrackingItemService,
    private projectService: ProjectService
  ) {}

  ngOnInit() {
    if (this.projectId) {
      this.loadItems();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectId'] && this.projectId) {
      this.loadItems();
    }
    if (changes['filterDate']) {
      this.applyDateFilter();
    }
  }

  /** Read the project's default payment term so the dialog can show what a blank field inherits. */
  private loadProjectPaymentTerm() {
    if (!this.projectId) {
      return;
    }
    this.projectService.getProjectById(this.projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (project) => {
        this.projectPaymentTermDays = project?.paymentTermDays ?? null;
      },
      // Non-fatal: the dialog just falls back to "no project default" wording.
      error: () => {
        this.projectPaymentTermDays = null;
      }
    });
  }

  loadItems() {
    if (!this.projectId) {
      return;
    }
    this.loadProjectPaymentTerm();
    this.loading = true;
    this.paymentTrackingItemService.getByProjectId(this.projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (items) => {
        this.items = items;
        this.applyDateFilter();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading payment tracking items:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load payment tracking items. Please try again.'
        });
        this.loading = false;
      }
    });
  }

  /**
   * Totals for the summary bar. `invoicedPercentage` deliberately mirrors the backend's
   * GetTotalInvoicedPercentageAsync - Invoiced + Paid only - so this tab and the API agree.
   * `plannedPercentage` is every milestone regardless of status, which is what a PM checking
   * "do my milestones add up to 100%" wants to see.
   */
  get invoicedPercentage(): number {
    return this.items
      .filter(i => i.invoicingStatus === 'Invoiced' || i.invoicingStatus === 'Paid')
      .reduce((sum, i) => sum + (i.percentage ?? 0), 0);
  }

  get plannedPercentage(): number {
    return this.items.reduce((sum, i) => sum + (i.percentage ?? 0), 0);
  }

  /** True when the milestones do not add up to 100% - worth surfacing to the PM. */
  get plannedIsUnbalanced(): boolean {
    return this.items.length > 0 && Math.abs(this.plannedPercentage - 100) > 0.01;
  }

  applyDateFilter() {
    if (!this.filterDate) {
      this.filteredItems = this.items;
      return;
    }

    const filterDateObj = this.parseLocalDateString(this.filterDate);
    if (!filterDateObj) {
      this.filteredItems = this.items;
      return;
    }
    const fy = filterDateObj.getFullYear();
    const fm = filterDateObj.getMonth();
    const fd = filterDateObj.getDate();

    this.filteredItems = this.items.filter(item => {
      if (!item.targetDate) return false;
      const itemDate = this.parseLocalDateString(item.targetDate);
      if (!itemDate) return false;
      return itemDate.getFullYear() === fy && itemDate.getMonth() === fm && itemDate.getDate() === fd;
    });
  }

  showAddItemDialog() {
    if (!this.projectId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Project ID is required to add payment tracking items.'
      });
      return;
    }
    this.selectedItem = {
      id: '',
      projectId: this.projectId,
      number: this.generateNumber(),
      paymentMilestone: '',
      percentage: 0,
      targetDate: '',
      invoicingStatus: 'Open',
      comments: ''
    };
    this.targetDate = null;
    this.invoicedDate = null;
    this.paymentDueDate = null;
    this.paidDate = null;
    this.fieldTouched = {};
    // Nothing entered yet, so the first calculated date is ours to keep updating.
    this.dueDateIsDerived = true;
    this.isEditItemMode = false;
    this.displayItemDialog = true;
  }

  showEditItemDialog(item: PaymentTrackingItem) {
    this.selectedItem = { ...item };
    this.targetDate = item.targetDate ? this.parseLocalDateString(item.targetDate) : null;
    this.invoicedDate = item.invoicedDate ? this.parseLocalDateString(item.invoicedDate) : null;
    this.paymentDueDate = item.paymentDueDate ? this.parseLocalDateString(item.paymentDueDate) : null;
    this.paidDate = item.paidDate ? this.parseLocalDateString(item.paidDate) : null;
    this.fieldTouched = {};
    // A saved due date is treated as the user's own: we cannot tell from the stored row whether it
    // was derived or an agreed extension, so the safe assumption is to preserve it. Clearing the
    // field re-arms auto-fill for anyone who wants it recalculated.
    this.dueDateIsDerived = !this.paymentDueDate;
    this.isEditItemMode = true;
    this.displayItemDialog = true;
  }

  resetItemForm() {
    this.fieldTouched = {};
    if (!this.selectedItem) return;
    if (this.isEditItemMode) {
      const item = this.filteredItems.find(i => i.id === this.selectedItem?.id) ?? this.items.find(i => i.id === this.selectedItem?.id);
      if (item) {
        this.selectedItem = { ...item };
        this.targetDate = item.targetDate ? this.parseLocalDateString(item.targetDate) : null;
      }
    } else {
      this.selectedItem.number = String((this.items.length + 1));
      this.selectedItem.paymentMilestone = '';
      this.selectedItem.percentage = 0;
      // 'Open' is the only valid starting status - 'Not Invoiced' is not in InvoicingStatuses
      // and the API now rejects it, so the form could never be saved.
      this.selectedItem.invoicingStatus = 'Open';
      this.selectedItem.comments = '';
      this.selectedItem.targetDate = undefined;
      this.selectedItem.invoicedDate = undefined;
      this.selectedItem.paymentDueDate = undefined;
      this.selectedItem.paymentTermDays = undefined;
      this.selectedItem.paidDate = undefined;
      this.targetDate = null;
      this.invoicedDate = null;
      this.paymentDueDate = null;
      this.paidDate = null;
    }
  }

  /**
   * Target Date is mandatory on Add but only warned about on Edit.
   *
   * The Invoicing Summary View places a milestone by its Target Date, so a row saved without one
   * is invisible to Finance. Blocking new rows stops that recurring. Blocking edits would not:
   * existing rows pre-date this rule, and someone opening one to flip its status would be forced
   * to invent a date they may not know - so there it is a warning only.
   */
  get targetDateMissing(): boolean {
    return !this.targetDate;
  }

  get targetDateIsBlocking(): boolean {
    return this.targetDateMissing && !this.isEditItemMode;
  }

  saveItem() {
    this.fieldTouched['paymentMilestone'] = true;
    this.fieldTouched['targetDate'] = true;

    if (!this.selectedItem || !this.selectedItem.paymentMilestone.trim()) {
      return;
    }

    if (this.targetDateIsBlocking) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Target Date required',
        detail: 'A milestone without a Target Date will not appear in the Invoicing Summary View.'
      });
      return;
    }

    if (!this.projectId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Project ID is required.'
      });
      return;
    }

    this.selectedItem.targetDate = this.targetDate ? this.formatDateToLocalString(this.targetDate) : undefined;
    this.selectedItem.invoicedDate = this.invoicedDate ? this.formatDateToLocalString(this.invoicedDate) : undefined;
    this.selectedItem.paymentDueDate = this.paymentDueDate ? this.formatDateToLocalString(this.paymentDueDate) : undefined;
    this.selectedItem.paidDate = this.paidDate ? this.formatDateToLocalString(this.paidDate) : undefined;

    if (this.isEditItemMode) {
      // Update existing item
      this.paymentTrackingItemService.updateItem(this.selectedItem.id, this.selectedItem).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updatedItem) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Payment tracking item updated successfully.'
          });
          // Optimistic update: replace item in local array by ID
          const index = this.items.findIndex(i => i.id === updatedItem.id);
          if (index !== -1) {
            this.items[index] = updatedItem;
          }
          this.applyDateFilter();
          this.displayItemDialog = false;
          this.selectedItem = null;
        },
        error: (error) => {
          console.error('Error updating payment tracking item:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update payment tracking item. Please try again.'
          });
        }
      });
    } else {
      // Create new item
      const createDto: CreatePaymentTrackingItemDto = {
        projectId: this.projectId,
        number: this.selectedItem.number,
        paymentMilestone: this.selectedItem.paymentMilestone,
        percentage: this.selectedItem.percentage,
        targetDate: this.selectedItem.targetDate,
        invoicedDate: this.selectedItem.invoicedDate,
        paymentDueDate: this.selectedItem.paymentDueDate,
        paymentTermDays: this.selectedItem.paymentTermDays,
        paidDate: this.selectedItem.paidDate,
        invoicingStatus: this.selectedItem.invoicingStatus,
        comments: this.selectedItem.comments
      };

      this.paymentTrackingItemService.addItem(createDto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (newItem) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Payment tracking item added successfully.'
          });
          // Optimistic update: push new item to local array
          this.items.push(newItem);
          this.applyDateFilter();
          this.displayItemDialog = false;
          this.selectedItem = null;
        },
        error: (error) => {
          console.error('Error adding payment tracking item:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add payment tracking item. Please try again.'
          });
        }
      });
    }
  }

  deleteItem(item: PaymentTrackingItem) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${item.paymentMilestone || ''}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.paymentTrackingItemService.deleteItem(item.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Payment tracking item deleted successfully.'
            });
            // Optimistic update: filter out deleted item from local array
            this.items = this.items.filter(i => i.id !== item.id);
            this.applyDateFilter();
          },
          error: (error) => {
            console.error('Error deleting payment tracking item:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete payment tracking item. Please try again.'
            });
          }
        });
      }
    });
  }

  generateNumber(): string {
    if (this.items.length === 0) {
      return '0001';
    }
    const maxNum = Math.max(...this.items.map(i => {
      const num = parseInt(i.number.replace(/\D/g, ''));
      return isNaN(num) ? 0 : num;
    }));
    return String(maxNum + 1).padStart(4, '0');
  }

  /** Parse YYYY-MM-DD as local date (avoids timezone shifting to previous/next day). */
  parseLocalDateString(dateStr: string): Date | null {
    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    const date = new Date(y, m, d);
    if (isNaN(date.getTime())) return null;
    return date;
  }

  /** Format a Date to YYYY-MM-DD using local date (avoids timezone shifting when saving). */
  formatDateToLocalString(date: Date): string {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = this.parseLocalDateString(dateString);
    if (!date) return '-';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Trims trailing zeros so 12.50 -> "12.5%" and 25.00 -> "25%", while still showing real
   * decimals. Previously returned the raw number, so a null rendered as "undefined%".
   */
  formatPercentage(percentage: number | null | undefined): string {
    if (percentage == null || isNaN(percentage)) {
      return '-';
    }
    return `${Number(percentage.toFixed(2))}%`;
  }

  getStatusSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'Open':
        return 'secondary';
      case 'Ready for Invoice':
        return 'warn';
      case 'Invoiced':
        return 'success';
      case 'Paid':
        return 'success';
      default:
        return 'secondary';
    }
  }

  /**
   * Payment term actually in force for the item being edited: this item's override when set,
   * otherwise the project default. Mirrors PaymentSettlement.ResolveTermDays on the server.
   */
  get effectiveTermDays(): number | null {
    const itemTerm = this.selectedItem?.paymentTermDays;
    if (itemTerm != null && itemTerm > 0) return itemTerm;
    return this.projectPaymentTermDays != null && this.projectPaymentTermDays > 0
      ? this.projectPaymentTermDays
      : null;
  }

  /**
   * Preview the derived due date in the dialog so the user sees the effect before saving.
   * <p>
   * Recalculates whenever the due date is blank OR still holds a value this method produced —
   * correcting a mistyped term is routine, and an earlier version that refused to touch any
   * non-empty due date made the term field look broken: typing 3 then 30 left the 3-day date in
   * place. A date the USER typed is still never overwritten; {@link dueDateIsDerived} is what
   * tells the two apart.
   * </p>
   */
  syncDerivedDueDate() {
    if (!this.invoicedDate) return;
    if (this.paymentDueDate && !this.dueDateIsDerived) return;

    const term = this.effectiveTermDays;
    if (term == null) return;

    const due = new Date(this.invoicedDate);
    due.setDate(due.getDate() + term);

    // Writing the model fires the datepicker's ngModelChange, which would otherwise run
    // onPaymentDueDateEdited and immediately mark our own value as user-owned.
    this.writingDerivedDueDate = true;
    this.paymentDueDate = due;
    this.dueDateIsDerived = true;
    this.writingDerivedDueDate = false;
  }

  /**
   * The user edited Payment Due Date directly — treat it as an agreed extension from now on, so a
   * later term or invoice-date change leaves it alone. Clearing the field re-arms auto-fill.
   */
  onPaymentDueDateEdited() {
    if (this.writingDerivedDueDate) return;

    this.dueDateIsDerived = !this.paymentDueDate;
    if (!this.paymentDueDate) {
      this.syncDerivedDueDate();
    }
  }

  /** Tag colour for the derived settlement status. */
  getPaymentStatusSeverity(status?: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'Overdue':
        return 'danger';
      case 'Paid Late':
        return 'warn';
      case 'Paid On Time':
      case 'Paid':
        return 'success';
      case 'Due':
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  /** "Overdue - 13 Days" / "Paid Late - 2 Days" / plain status otherwise. */
  getPaymentStatusLabel(item: PaymentTrackingItem): string {
    const status = item.paymentStatus;
    if (!status || status === 'Not Applicable') return '-';
    if (item.overdueDays != null && item.overdueDays > 0) {
      const unit = item.overdueDays === 1 ? 'Day' : 'Days';
      return `${status} - ${item.overdueDays} ${unit}`;
    }
    return status;
  }

  getPaymentStatusTooltip(item: PaymentTrackingItem): string {
    switch (item.paymentStatus) {
      case 'Overdue':
        return `Unpaid since the due date of ${this.formatDate(item.paymentDueDate)}`;
      case 'Paid Late':
        return `Paid ${this.formatDate(item.paidDate)}, after the due date of ${this.formatDate(item.paymentDueDate)}`;
      case 'Paid On Time':
        return `Paid ${this.formatDate(item.paidDate)}`;
      case 'Paid':
        return 'Marked as Paid. Add a Paid Date to show whether it was settled on time.';
      case 'Due':
        return `Payment due ${this.formatDate(item.paymentDueDate)}`;
      default:
        return 'No payment due date — not invoiced yet, or no payment term set';
    }
  }

  onDialogVisibleChange(visible: boolean) {
    // Only update if explicitly closing (not from select dropdown interactions)
    if (!visible) {
      this.displayItemDialog = false;
      this.selectedItem = null;
      this.targetDate = null;
    } else {
      this.displayItemDialog = true;
    }
  }
}

